import { CreditCardIcon, PencilIcon, XIcon } from 'lucide-react';

import React, { type ReactNode, useState } from 'react';

import { useTranslation } from '@splitfiesta/i18n';
import {
  GroupMemberResponseDto,
  formatAmountForCurrency,
} from '@splitfiesta/shared';
import { Avatar, Button, Card } from '@splitfiesta/ui';

import MultiPayerSelect from '@/components/ui/MultiPayerSelect';

// Using Payer interface from expense DTOs
interface Payer {
  userId: string;
  amount: number;
}

// Member type that handles both populated and unpopulated userId fields
type Member =
  | GroupMemberResponseDto
  | {
      userId: string | { _id: string; name: string; email: string };
      name?: string;
      email?: string;
    };

interface ExpensePaymentDetailsProps {
  members: Member[];
  payers: Payer[];
  setPayers: (payers: Payer[]) => void;
  totalAmount: number;
  currency: string;
  currentUserId?: string;
  isLoading?: boolean;
  error?: string;
  /** Under the payer while one person paid: adds someone not in the group yet. */
  saveSpot?: ReactNode;
}

export function ExpensePaymentDetails({
  members,
  payers,
  setPayers,
  totalAmount,
  currency,
  currentUserId,
  isLoading = false,
  error,
  saveSpot,
}: ExpensePaymentDetailsProps) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  // Collapsed view when amount is entered and not expanded
  if (totalAmount > 0 && !isExpanded) {
    return (
      <Card
        className="space-y-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
        interactive
        noHoverEffect
      >
        <div
          onClick={() => !isLoading && setIsExpanded(true)}
          role="button"
          tabIndex={0}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              if (!isLoading) setIsExpanded(true);
            }
          }}
        >
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-2">
              <CreditCardIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t('expenses:sections.paidBySection')}
              </h3>
            </div>
            <Button
              variant="primary"
              size="sm"
              type="button"
              onClick={e => {
                e.stopPropagation();
                setIsExpanded(true);
              }}
              disabled={isLoading}
              leftIcon={<PencilIcon className="h-4 w-4" />}
              className="shrink-0"
            >
              {t('common:actions.edit')}
            </Button>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('expenses:descriptions.whoPaidHelper')}
          </p>
        </div>

        {/* Show who paid in collapsed state - one row per payer */}
        <div className="space-y-2">
          {payers
            .filter(p => p.amount > 0)
            .map(payer => {
              const payerMember = members.find(m => {
                const memberId =
                  typeof m.userId === 'string' ? m.userId : m.userId._id;
                return memberId === payer.userId;
              });
              const displayAmount =
                payers.filter(p => p.amount > 0).length === 1 && totalAmount > 0
                  ? totalAmount
                  : payer.amount;
              return (
                <div
                  key={payer.userId}
                  className="flex items-center justify-between"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Avatar
                      name={payerMember?.name || 'Unknown'}
                      src={
                        (payerMember as { profileImage?: string })?.profileImage
                      }
                      size="xs"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300 truncate">
                      {payerMember?.name || 'Unknown'}
                    </span>
                  </div>
                  <span className="text-sm font-bold text-gray-900 dark:text-gray-100 shrink-0">
                    {formatAmountForCurrency(displayAmount, currency)}
                  </span>
                </div>
              );
            })}
        </div>
      </Card>
    );
  }

  // Full view
  return (
    <Card className="space-y-4">
      <div>
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            <CreditCardIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {t('expenses:sections.paidBySection')}
            </h3>
          </div>
          {totalAmount > 0 && (
            <Button
              variant="outline"
              size="sm"
              type="button"
              onClick={() => setIsExpanded(false)}
              disabled={isLoading}
              leftIcon={<XIcon className="h-4 w-4" />}
              className="shrink-0"
            >
              {t('common:actions.close')}
            </Button>
          )}
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
          {t('expenses:descriptions.whoPaidHelper')}
        </p>
      </div>

      {totalAmount === 0 ? (
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
            {t('expenses:messages.enterAmountToSelectPayers')}
          </p>
        </div>
      ) : (
        <MultiPayerSelect
          members={members}
          value={payers}
          onChange={setPayers}
          totalAmount={totalAmount}
          currency={currency}
          currentUserId={currentUserId}
          disabled={isLoading}
          error={error}
          saveSpot={saveSpot}
        />
      )}
    </Card>
  );
}

export default ExpensePaymentDetails;
